/**
 * First-touch referral invite capture (?ref=CODE).
 *
 * Stores code in localStorage (30-day window) and a cross-subdomain cookie
 * (`{code, timestamp}`) so Framer landing → app.ofertator.pl still carries
 * the referral without extending the original first-touch window.
 * Explicit user input overwrites via setStoredReferralCode.
 */

import { getRuntimeEnv } from '../../lib/runtime-env'

export const STORAGE_KEY = 'ofertator_referral_code'
export const COOKIE_KEY = 'of_referral_code'
export const REFERRAL_WINDOW_DAYS = 30
export const REF_PARAM_KEYS = ['ref', 'referral', 'referral_code'] as const
export const DEFAULT_LANDING_URL = 'https://ofertator.pl/'

const CODE_PATTERN = /^[A-Za-z0-9_-]{3,32}$/
const COOKIE_DOMAIN = '.ofertator.pl'

interface StoredReferral {
  code: string
  timestamp: number
}

export function normalizeReferralCode(raw: string | null | undefined): string | null {
  if (raw == null) return null
  const trimmed = raw.trim().toUpperCase()
  if (!trimmed || !CODE_PATTERN.test(trimmed)) return null
  return trimmed
}

export function pickReferralFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
  for (const key of REF_PARAM_KEYS) {
    const value = normalizeReferralCode(params.get(key))
    if (value) return value
  }
  return null
}

/**
 * Canonical partner/user invite URL → marketing landing with ?ref=.
 * Framer snippet stores of_referral_code and appends ref to app.ofertator.pl links.
 */
export function buildReferralInviteUrl(code: string, landingBase?: string): string {
  const normalized = normalizeReferralCode(code)
  const raw =
    landingBase ??
    getRuntimeEnv('VITE_LANDING_URL') ??
    DEFAULT_LANDING_URL
  const base = raw.replace(/\/$/, '')
  if (!normalized) return `${base}/`
  return `${base}/?ref=${encodeURIComponent(normalized)}`
}

export function isReferralExpired(timestamp: number, now = Date.now()): boolean {
  const ageDays = (now - timestamp) / (1000 * 60 * 60 * 24)
  return ageDays > REFERRAL_WINDOW_DAYS
}

/** Parse cookie payload; rejects expired / invalid. Exported for tests. */
export function parseReferralCookiePayload(
  raw: string | null | undefined,
  now = Date.now(),
): StoredReferral | null {
  if (raw == null || raw === '') return null

  try {
    const parsed = JSON.parse(raw) as { code?: unknown; timestamp?: unknown }
    if (typeof parsed?.code === 'string' && typeof parsed?.timestamp === 'number') {
      const code = normalizeReferralCode(parsed.code)
      if (!code) return null
      if (isReferralExpired(parsed.timestamp, now)) return null
      return { code, timestamp: parsed.timestamp }
    }
  } catch {
    // Legacy plain-string cookie (no timestamp) — cannot prove first-touch age; drop it.
    return null
  }
  return null
}

function cookieDomainForHost(hostname: string): string | null {
  if (hostname === 'ofertator.pl' || hostname.endsWith('.ofertator.pl')) {
    return COOKIE_DOMAIN
  }
  return null
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

function writeReferralCookie(stored: StoredReferral): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const domain = cookieDomainForHost(window.location.hostname)
  const maxAge = REFERRAL_WINDOW_DAYS * 24 * 60 * 60
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const domainPart = domain ? `; domain=${domain}` : ''
  const payload = JSON.stringify({ code: stored.code, timestamp: stored.timestamp })
  document.cookie =
    `${COOKIE_KEY}=${encodeURIComponent(payload)}` +
    `${domainPart}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`
}

function clearReferralCookie(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const domain = cookieDomainForHost(window.location.hostname)
  const domainPart = domain ? `; domain=${domain}` : ''
  // Clear both dotted and bare domain variants (Framer historically used ofertator.pl).
  document.cookie = `${COOKIE_KEY}=; path=/; max-age=0${domainPart}`
  if (domain) {
    document.cookie = `${COOKIE_KEY}=; path=/; max-age=0; domain=ofertator.pl`
  }
}

function parseStored(raw: string | null): StoredReferral | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredReferral
    if (parsed && typeof parsed.code === 'string' && typeof parsed.timestamp === 'number') {
      const code = normalizeReferralCode(parsed.code)
      if (!code) return null
      return { code, timestamp: parsed.timestamp }
    }
  } catch {
    // Legacy plain-string storage
  }
  const legacy = normalizeReferralCode(raw)
  if (!legacy) return null
  return { code: legacy, timestamp: Date.now() }
}

function readStoredReferral(now = Date.now()): StoredReferral | null {
  try {
    const stored = parseStored(localStorage.getItem(STORAGE_KEY))
    if (!stored) return null
    if (isReferralExpired(stored.timestamp, now)) {
      localStorage.removeItem(STORAGE_KEY)
      clearReferralCookie()
      return null
    }
    return stored
  } catch {
    return null
  }
}

function readReferralFromCookie(now = Date.now()): StoredReferral | null {
  const raw = readCookie(COOKIE_KEY)
  const parsed = parseReferralCookiePayload(raw, now)
  if (raw && !parsed) {
    // Expired, legacy, or corrupt — drop so it cannot revive LS.
    clearReferralCookie()
  }
  return parsed
}

function writeStoredReferral(code: string, timestamp = Date.now()): void {
  const stored: StoredReferral = { code, timestamp }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // ignore
  }
  writeReferralCookie(stored)
}

/** Exported for unit tests — first-touch store decision. */
export function resolveFirstTouchCode(opts: {
  fromQuery: string | null
  fromCookie: string | null
  existing: StoredReferral | null
  now?: number
}): string | null {
  if (opts.existing && !isReferralExpired(opts.existing.timestamp, opts.now)) {
    return null
  }
  return opts.fromQuery ?? opts.fromCookie
}

function stripInviteParamsFromUrl(): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    let changed = false
    for (const key of REF_PARAM_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key)
        changed = true
      }
    }
    if (changed) {
      const next = `${url.pathname}${url.search}${url.hash}`
      window.history.replaceState(window.history.state, '', next)
    }
  } catch {
    // ignore
  }
}

export function getStoredReferralCode(): string {
  return readStoredReferral()?.code ?? ''
}

/** Explicit user input / form overwrite (last-touch). */
export function setStoredReferralCode(code: string): void {
  const normalized = normalizeReferralCode(code)
  if (!normalized) {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
    clearReferralCookie()
    return
  }
  writeStoredReferral(normalized)
}

export function clearStoredReferralCode(): void {
  setStoredReferralCode('')
}

export class ReferralInviteService {
  /**
   * Capture referral from URL or cookie (first-touch only).
   * Call synchronously before React render.
   * Cookie timestamp is preserved so rehydration does not extend the 30-day window.
   */
  static captureFromEnvironment(): void {
    if (typeof window === 'undefined') return

    const now = Date.now()
    const fromQuery = pickReferralFromSearch(window.location.search)
    const fromCookie = readReferralFromCookie(now)
    const existing = readStoredReferral(now)

    if (existing) {
      if (fromQuery) stripInviteParamsFromUrl()
      return
    }

    if (fromQuery) {
      writeStoredReferral(fromQuery, now)
      stripInviteParamsFromUrl()
      return
    }

    if (fromCookie) {
      writeStoredReferral(fromCookie.code, fromCookie.timestamp)
    }
  }

  static getCode(): string {
    return getStoredReferralCode()
  }

  static clear(): void {
    clearStoredReferralCode()
  }

  static buildInviteUrl(code: string, landingBase?: string): string {
    return buildReferralInviteUrl(code, landingBase)
  }
}
